import { Controller } from '@hotwired/stimulus';
import { showToast } from '../lib/toast';

const normalize = (value) =>
  value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Fuzzy match: substring, or all query chars appearing in order (subsequence).
const fuzzyMatch = (query, haystack) => {
  if (!query) return true;
  if (haystack.includes(query)) return true;

  let i = 0;
  for (const char of haystack) {
    if (char === query[i]) i += 1;
    if (i === query.length) return true;
  }
  return false;
};

const FIELDS = ['title', 'description', 'seed_value'];

// Variables drawer: client-side search + pagination over a seeded list, plus
// inline add/edit submitted via fetch to the JSON endpoints. Rows are cloned
// from a <template> so mutations never reload the page (editor content is kept).
export default class extends Controller {
  static values = {
    variables: Array,
    indexPath: String,
    perPage: Number,
    strings: Object,
  };

  static targets = [
    'count', 'listButton', 'addButton',
    'listMode', 'searchWrap', 'search', 'searchClear', 'list',
    'emptyResults', 'emptyAll', 'foot', 'perPage', 'pagerInfo', 'prev', 'next',
    'rowTemplate',
    'form', 'formTitle', 'callout', 'calloutInfo', 'calloutWarn', 'calloutText',
    'input', 'check', 'error', 'submit', 'submitSpinner', 'submitLabel',
  ];

  connect() {
    this.items = this.variablesValue || [];
    this.strings = this.stringsValue || {};
    this.query = '';
    this.page = 0;
    this.perPage = this.perPageValue || 5;
    this.editingId = null;
    this.showList();
    this.syncEditor();
  }

  // ---- view modes ----------------------------------------------------------

  showList() {
    this.mode = 'list';
    this.formTarget.hidden = true;
    this.listModeTarget.hidden = false;
    this.setActiveTool('list');
    this.renderList();
  }

  showAdd() {
    this.mode = 'add';
    this.editingId = null;
    this.resetForm();
    this.formTitleTarget.textContent = this.strings.add_title || '';
    this.calloutTextTarget.textContent = this.strings.add_callout || '';
    this.calloutInfoTarget.classList.remove('hidden');
    this.calloutWarnTarget.classList.add('hidden');
    this.openForm();
    this.setActiveTool('add');
    this.validate();
  }

  showEdit(event) {
    const row = event.target.closest('.ds-tpled-row');
    const variable = this.items.find((item) => String(item.id) === String(row?.dataset.id));
    if (!variable) return;

    this.mode = 'edit';
    this.editingId = variable.id;
    this.resetForm();
    FIELDS.forEach((name) => {
      const input = this.inputFor(name);
      if (input) input.value = variable[name] || '';
    });
    this.formTitleTarget.textContent = this.strings.edit_title || '';
    this.calloutTextTarget.textContent = this.strings.edit_callout || '';
    this.calloutInfoTarget.classList.add('hidden');
    this.calloutWarnTarget.classList.remove('hidden');
    this.openForm();
    this.setActiveTool(null);
    this.validate();
  }

  closeForm() {
    this.showList();
  }

  openForm() {
    this.listModeTarget.hidden = true;
    this.formTarget.hidden = false;
  }

  setActiveTool(which) {
    this.listButtonTarget.classList.toggle('is-active', which === 'list');
    this.addButtonTarget.classList.toggle('is-active', which === 'add');
  }

  // ---- search & pagination -------------------------------------------------

  search() {
    this.query = this.searchTarget.value;
    this.page = 0;
    this.searchClearTarget.hidden = this.query.trim().length === 0;
    this.renderList();
  }

  clearSearch() {
    this.searchTarget.value = '';
    this.query = '';
    this.searchClearTarget.hidden = true;
    this.renderList();
    this.searchTarget.focus();
  }

  changePerPage() {
    this.perPage = parseInt(this.perPageTarget.value, 10) || 5;
    this.page = 0;
    this.renderList();
  }

  previousPage() {
    if (this.page > 0) {
      this.page -= 1;
      this.renderList();
    }
  }

  nextPage() {
    this.page += 1;
    this.renderList();
  }

  // ---- rendering -----------------------------------------------------------

  filtered() {
    const query = normalize(this.query.trim());
    if (!query) return this.items;
    return this.items.filter((item) =>
      fuzzyMatch(query, normalize(`${item.title} ${item.description || ''}`)));
  }

  renderList() {
    const total = this.items.length;
    const matches = this.filtered();
    const pageCount = Math.max(1, Math.ceil(matches.length / this.perPage));
    if (this.page > pageCount - 1) this.page = pageCount - 1;
    if (this.page < 0) this.page = 0;

    const start = this.page * this.perPage;
    const slice = matches.slice(start, start + this.perPage);

    this.listTarget.replaceChildren();
    slice.forEach((item) => this.listTarget.appendChild(this.buildRow(item)));

    const noneAtAll = total === 0;
    const noResults = !noneAtAll && matches.length === 0;
    this.emptyAllTarget.hidden = !noneAtAll;
    this.emptyResultsTarget.hidden = !noResults;
    this.searchWrapTarget.hidden = noneAtAll;
    this.listTarget.hidden = noneAtAll || noResults;
    this.footTarget.hidden = noneAtAll || noResults;

    this.updateCount(matches.length, total);
    this.updatePager(matches.length);
  }

  buildRow(variable) {
    const node = this.rowTemplateTarget.content.firstElementChild.cloneNode(true);
    node.dataset.id = variable.id;
    const name = node.querySelector('[data-row="name"]');
    const desc = node.querySelector('[data-row="desc"]');
    name.textContent = variable.title;
    desc.textContent = variable.description || '';
    if (this.query.trim()) {
      this.highlight(name, variable.title);
      this.highlight(desc, variable.description || '');
    }
    return node;
  }

  highlight(element, text) {
    const index = normalize(text).indexOf(normalize(this.query.trim()));
    if (index === -1) return;

    const length = this.query.trim().length;
    const mark = document.createElement('mark');
    mark.className = 'ds-tpl-mark';
    mark.textContent = text.slice(index, index + length);
    element.replaceChildren(
      document.createTextNode(text.slice(0, index)),
      mark,
      document.createTextNode(text.slice(index + length)),
    );
  }

  updateCount(visible, total) {
    if (this.query.trim()) {
      const stencil = this.countTarget.dataset.countFiltered || 'COUNT / TOTAL';
      this.countTarget.textContent = stencil
        .replace('COUNT', visible).replace('TOTAL', total);
    } else {
      this.countTarget.textContent = String(total);
    }
  }

  updatePager(length) {
    const from = length === 0 ? 0 : this.page * this.perPage + 1;
    const to = Math.min(length, (this.page + 1) * this.perPage);
    const stencil = this.pagerInfoTarget.dataset.pager || 'FROM–TO / TOTAL';
    this.pagerInfoTarget.textContent = stencil
      .replace('FROM', from).replace('TO', to).replace('TOTAL', length);
    this.prevTarget.disabled = this.page === 0;
    this.nextTarget.disabled = to >= length;
  }

  // ---- form ----------------------------------------------------------------

  inputFor(name) {
    return this.inputTargets.find((input) => input.dataset.variableField === name);
  }

  resetForm() {
    this.inputTargets.forEach((input) => {
      input.value = '';
      input.classList.remove('is-valid', 'is-invalid');
    });
    this.checkTargets.forEach((check) => check.classList.add('hidden'));
    this.errorTargets.forEach((error) => {
      error.hidden = true;
      error.textContent = '';
    });
  }

  validate() {
    let allValid = true;
    this.inputTargets.forEach((input, index) => {
      const filled = input.value.trim().length > 0;
      input.classList.toggle('is-valid', filled);
      if (filled) input.classList.remove('is-invalid');
      if (this.checkTargets[index]) {
        this.checkTargets[index].classList.toggle('hidden', !filled);
      }
      if (this.errorTargets[index]) {
        this.errorTargets[index].hidden = true;
      }
      if (!filled) allValid = false;
    });
    this.submitTarget.disabled = !allValid;
    return allValid;
  }

  submit(event) {
    event.preventDefault();
    if (!this.validate()) return;

    const fields = {};
    FIELDS.forEach((name) => { fields[name] = this.inputFor(name).value; });

    const url = this.mode === 'edit'
      ? `${this.indexPathValue}/${this.editingId}`
      : this.indexPathValue;
    const method = this.mode === 'edit' ? 'PUT' : 'POST';

    this.setSubmitting(true);
    this.persist(method, url, fields)
      .then((response) => this.handleResponse(response))
      .catch(() => {
        this.setSubmitting(false);
        showToast('error', this.strings.error || '');
      });
  }

  async handleResponse(response) {
    if (response.status === 200) {
      await this.refresh();
      this.setSubmitting(false);
      showToast('success',
        this.mode === 'edit' ? this.strings.updated : this.strings.created);
      this.closeForm();
    } else if (response.status === 422) {
      const data = await response.json();
      this.setSubmitting(false);
      this.showErrors(data.error || {});
    } else {
      this.setSubmitting(false);
      showToast('error', this.strings.error || '');
    }
  }

  persist(method, url, fields) {
    return fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken(),
      },
      body: JSON.stringify({ variable: fields }),
    });
  }

  showErrors(errors) {
    FIELDS.forEach((name, index) => {
      const messages = errors[name];
      const input = this.inputFor(name);
      const error = this.errorTargets[index];
      if (messages && messages.length) {
        input.classList.add('is-invalid');
        input.classList.remove('is-valid');
        if (error) {
          error.textContent = messages.join(', ');
          error.hidden = false;
        }
      } else if (error) {
        error.hidden = true;
      }
    });
  }

  setSubmitting(submitting) {
    this.submitTarget.disabled = submitting;
    this.submitSpinnerTarget.classList.toggle('hidden', !submitting);
    this.submitLabelTarget.textContent = submitting
      ? (this.strings.sending || '') : (this.strings.submit || '');
  }

  // ---- refresh / editor sync ----------------------------------------------

  async refresh() {
    const response = await fetch(this.indexPathValue, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;

    const data = await response.json();
    this.items = data.variables || [];
    if (this.mode === 'list') this.renderList();
    this.syncEditor();
  }

  // Broadcast the full variable objects: the editor maps them to titles for its
  // autocomplete, the bulk-edit modal rebuilds its rows from them.
  syncEditor() {
    window.dispatchEvent(
      new CustomEvent('tpled:variables-synced', { detail: { variables: this.items } }),
    );
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  // window "tpled:open-bulk" target is the modal; the drawer just relays the
  // click so the button can live in the drawer header.
  openBulk() {
    window.dispatchEvent(new CustomEvent('tpled:open-bulk'));
  }
}
